import React from 'react';
import { useTranslation } from 'react-i18next';
import { JobOffer } from '../../models/job';
import { Document } from '../../models/job';
import { 
  FileText, 
  File, 
  Briefcase, 
  Building2, 
  Code2, 
  CheckCircle, 
  Loader2, 
  AlertCircle,
  X
} from 'lucide-react';
import { Button } from '../ui/atoms/Button';
import { Tag } from '../ui/atoms/Tag';
import { ProgressBar } from '../ui/atoms/ProgressBar';
import { formatDateTime } from '@/utils/date';

interface UploadedCvsModalProps {
  job: JobOffer;
  isOpen: boolean;
  onClose: () => void;
}

// Helper function to get document icon based on type
const getDocumentIcon = (documentType: string | null | undefined) => {
  if (!documentType) {
    return <File className="w-5 h-5" />;
  }
  
  switch (documentType.toLowerCase()) {
    case 'cv':
      return <FileText className="w-5 h-5" />;
    case 'cover_letter':
      return <File className="w-5 h-5" />;
    case 'job_description':
      return <Briefcase className="w-5 h-5" />;
    case 'company_profile':
      return <Building2 className="w-5 h-5" />;
    case 'code_extracts':
      return <Code2 className="w-5 h-5" />;
    case 'other':
    default:
      return <File className="w-5 h-5" />;
  }
};

const getStatusIntent = (status: string | null | undefined) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'success' as const;
    case 'processing':
      return 'warning' as const;
    case 'pending':
      return 'info' as const;
    case 'failed':
      return 'error' as const;
    default:
      return 'secondary' as const;
  }
};

// Helper function to get document type display name
const getDocumentTypeDisplayName = (documentType: string | null | undefined): string => {
  if (!documentType) {
    return 'Unknown';
  }
  
  switch (documentType.toLowerCase()) {
    case 'cv':
      return 'CV';
    case 'cover_letter':
      return 'Cover Letter';
    case 'job_description':
      return 'Job Description';
    case 'company_profile':
      return 'Company Profile';
    case 'code_extracts':
      return 'Code Extracts';
    case 'other':
      return 'Other';
    default:
      return documentType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
};

export const UploadedCvsModal: React.FC<UploadedCvsModalProps> = ({
  job,
  isOpen,
  onClose
}) => {
  const { t } = useTranslation('job');
  if (!isOpen) return null;

  const documents = job.documents || [];

  const sortedDocuments = [...documents].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-4xl mx-auto w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h5>Uploaded Documents</h5>
          <Button
              type="icon"
              intent="muted"
              onClick={onClose}
              icon={<X/>}
              label="Close"
            />
        </div>

        <div className="space-y-4">
          {/* Summary Statistics */}
          <div className="bg-background-gray rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-dark-invirtus font-heading">{documents.length}</div>
                <div className="text-sm text-dark-invirtus font-body">Total Documents</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 font-heading">
                  {documents.filter(doc => doc.processingStatus === 'completed').length}
                </div>
                <div className="text-sm text-dark-invirtus font-body">Completed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600 font-heading">
                  {documents.filter(doc => doc.processingStatus === 'processing' || doc.processingStatus === 'pending').length}
                </div>
                <div className="text-sm text-dark-invirtus font-body">Processing</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600 font-heading">
                  {documents.filter(doc => doc.processingStatus === 'failed').length}
                </div>
                <div className="text-sm text-dark-invirtus font-body">Failed</div>
              </div>
            </div>
          </div>

          {/* Documents List */}
          <div className="bg-white border border-gray-200 rounded-lg">
            {documents.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {sortedDocuments.map((doc: Document) => (
                  <div key={doc.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start space-x-4">
                      {/* Document Icon */}
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-gradient-1 rounded-lg flex items-center justify-center">
                          <div className="text-dark-invirtus">
                            {getDocumentIcon(doc.documentType)}
                          </div>
                        </div>
                      </div>

                      {/* Document Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-dark-invirtus truncate font-body">
                            {doc.fileName}
                          </h4>
                          <div className="flex items-center space-x-2">
                            {/* Status Badge */}
                            <Tag
                              intent={getStatusIntent(doc.processingStatus)}
                              variant="subtle"
                              icon={
                                doc.processingStatus === 'completed'
                                  ? <CheckCircle className="w-3 h-3" />
                                  : doc.processingStatus === 'processing'
                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                    : doc.processingStatus === 'failed'
                                      ? <AlertCircle className="w-3 h-3" />
                                      : undefined
                              }
                            >
                              {doc.processingStatus}
                            </Tag>
                          </div>
                        </div>

                        {/* Document Metadata */}
                        <div className="mt-2 flex items-center justify-between text-sm text-gray-500 font-body">
                          <div className="flex items-center space-x-4">
                            <span>{getDocumentTypeDisplayName(doc.documentType)}</span>
                            <span>•</span>
                            <span>{formatDateTime(doc.createdAt)}</span>
                            {doc.fileSize && (
                              <>
                                <span>•</span>
                                <span>{(doc.fileSize / 1024 / 1024).toFixed(2)} MB</span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Error Message (if failed) */}
                        {/*d oc.processingStatus === 'failed' && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                            <div className="flex items-start">
                              <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                              <span>Document processing failed. Please check the file format and try again.</span>
                            </div>
                          </div>
                        )*/}

                        {/* Processing Progress (if processing) */}
                        {doc.processingStatus === 'processing' && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-1 font-body">
                              <span>{t('uploaded_cvs_modal.processing')}</span>
                              <span>{t('uploaded_cvs_modal.processing_wait')}</span>
                            </div>
                            <ProgressBar
                              isIndeterminate
                              size="xs"
                              intent="primary"
                              aria-label={t('uploaded_cvs_modal.processing_aria')}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <File className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-dark-invirtus mb-2 font-heading">No Documents Uploaded</h3>
                <p className="text-gray-500 font-body">No documents have been uploaded for this job yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
          <Button
              type="outlined"
              intent="secondary"
              onClick={onClose}
              label="Close"
          />
        </div>
      </div>
    </div>
  );
};

export default UploadedCvsModal;
